"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Calendar as CalendarIcon } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [dateOpen, setDateOpen] = useState(false);
  if (isSubmitted && !submitError) {
    return <div className="text-center py-16 px-8">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-6">— Received —</p>
        <h3 className="font-default text-3xl md:text-4xl text-foreground mb-6 leading-tight">
          Thank you. Your request <em>has been received</em>.
        </h3>
        <p className="font-default italic text-lg text-muted-foreground max-w-md mx-auto mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg px-6">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 font-default text-lg focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50" />
            </FormControl>
            <FormMessage className="font-default italic text-destructive" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 font-default text-lg focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50" />
            </FormControl>
            <FormMessage className="font-default italic text-destructive" />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 font-default text-lg focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50" />
            </FormControl>
            <FormMessage className="font-default italic text-destructive" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="preferred_date" rules={{
        required: "Please choose a date"
      }} render={({
        field
      }) => <FormItem className="gap-1 flex flex-col">
              <FormLabel className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                Preferred Date
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Button type="button" variant="ghost" onClick={() => setDateOpen(!dateOpen)} className="w-full justify-between border-0 border-b border-border bg-transparent px-0 h-11 font-default text-lg hover:bg-transparent hover:text-foreground">
                    <span className={field.value ? "text-foreground" : "text-muted-foreground/50"}>
                      {field.value ? new Date(field.value).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric"
                }) : "Select a date"}
                    </span>
                    <SafeHydrate><CalendarIcon className="size-4 text-muted-foreground" /></SafeHydrate>
                  </Button>
                  {dateOpen && <div className="absolute z-20 mt-2 bg-card border border-border rounded-lg shadow-lg">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                  field.onChange(date ? date.toISOString().split("T")[0] : "");
                  setDateOpen(false);
                }} /></SafeHydrate>
                    </div>}
                </div>
              </FormControl>
              <FormMessage className="font-default italic text-destructive" />
            </FormItem>} />

        <FormField control={control} name="preferred_time" rules={{
        required: "Please choose a time slot"
      }} render={({
        field
      }) => <FormItem className="gap-1">
              <FormLabel className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                Preferred Time Slot
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full border-0 border-b border-border bg-transparent px-0 h-11 font-default text-lg focus:ring-0 data-[placeholder]:text-muted-foreground/50">
                    <SelectValue placeholder="Select a time slot" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="Morning (9 am – 12 pm)" className="font-default">Morning (9 am – 12 pm)</SelectItem>
                    <SelectItem value="Afternoon (12 pm – 3 pm)" className="font-default">Afternoon (12 pm – 3 pm)</SelectItem>
                    <SelectItem value="Late Afternoon (3 pm – 6 pm)" className="font-default">Late Afternoon (3 pm – 6 pm)</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage className="font-default italic text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_of_interest" rules={{
      required: "Please choose a service"
    }} render={({
      field
    }) => <FormItem className="gap-3 pt-2">
            <FormLabel className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Service of Interest
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                {["Software", "Hardware", "Consulting"].map((option, i) => <label data-index={i} key={option} htmlFor={`service-${option}`} className="flex items-center gap-3 border border-border rounded-lg px-4 py-3 cursor-pointer hover:border-primary transition-colors has-[:checked]:border-primary has-[:checked]:bg-accent/40">
                    <RadioGroupItem value={option} id={`service-${option}`} />
                    <span className="font-default text-base text-foreground">{option}</span>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage className="font-default italic text-destructive" />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Project Requirements
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Describe your technical needs, project scope, or specific goals." {...field} className="border-0 border-b border-border bg-transparent px-0 py-2 font-default text-lg focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 resize-none" />
            </FormControl>
            <p className="font-default italic text-sm text-muted-foreground pt-1">
              Optional — helps us prepare for your technical discovery.
            </p>
            <FormMessage className="font-default italic text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/30 rounded-lg bg-destructive/5">
          <p className="font-default italic text-destructive">{submitError}</p>
        </div>}

      <div className="pt-6 space-y-4">
        <Button type="submit" disabled={isSubmitting} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-7 h-12 font-mono text-xs tracking-[0.2em] uppercase">
          {isSubmitting ? "Submitting..." : "Submit Request"}
          <ArrowRight className="size-4 ml-1" />
        </Button>
        <p className="font-default italic text-sm text-muted-foreground">
          A member of our engineering team will respond within two business days.
        </p>
      </div>
    </Form>;
}
export default function RequestAConsultation() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const formCard = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const bringList = ["existing system architecture", "project scope documentation", "defined business objectives"];
  const hours = [{
    day: "Monday — Friday",
    time: "9 am — 6 pm"
  }, {
    day: "Saturday",
    time: "By appointment"
  }, {
    day: "Sunday",
    time: "Closed"
  }];
  return <section data-section-id="4818" id="request-a-consultation" className="bg-background py-24 md:py-32 overflow-hidden light">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div ref={intro.ref} className={`lg:col-span-5 lg:pt-6 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-10">
              Bookings — Technical Discovery
            </p>

            <h2 className="font-default text-5xl md:text-6xl lg:text-[4rem] leading-[1.05] text-foreground mb-10">
              Start your<br />
              <em className="text-primary">digital</em> transformation.
            </h2>

            <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed mb-12 max-w-md">
              Expect ninety minutes of deep-dive technical analysis. We review your current infrastructure, identify bottlenecks, and architect a roadmap for your business growth.
            </p>

            <div className="mb-14">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
                What to prepare
              </p>
              <ul className="space-y-3">
                {bringList.map((item, i) => <li data-index={i} key={i} className="flex items-baseline gap-4 font-default italic text-lg text-foreground">
                    <span className="text-primary select-none">—</span>
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-5">
                Office Hours
              </p>
              <div className="space-y-0 max-w-sm">
                {hours.map((h, i) => <div data-index={i} key={i} className="flex justify-between items-baseline py-3 border-t border-border last:border-b">
                    <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{h.day}</span>
                    <span className="font-default text-base text-foreground">{h.time}</span>
                  </div>)}
              </div>
            </div>
          </div>

          <div ref={formCard.ref} className={`lg:col-span-7 lg:col-start-6 transition-all duration-1000 delay-150 ease-out ${formCard.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="consultation_form_home" formId="consultation_form_home" sectionName="request-a-consultation" className="bg-accent/30 rounded-lg p-8 md:p-12 lg:p-14 shadow-md border border-border/40" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            preferred_date: "",
            preferred_time: "",
            service_of_interest: "",
            details: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            preferred_date: {
              required: true
            },
            preferred_time: {
              required: true
            },
            service_of_interest: {
              required: true
            }
          }} submitText="Submit Request" successMessage="A member of our engineering team will be in touch within two business days to confirm your discovery session." errorMessage="We could not submit your request. Please try again, or contact us directly.">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
                Request a Discovery
              </p>
              <h3 className="font-default text-3xl md:text-4xl text-foreground mb-10 leading-tight">
                Tell us <em>a little</em> about your project.
              </h3>
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}